<template>
  <footer id="footer" ref="footerRef" class="footer">
    <div class="footer__main">
      <!-- Left Content -->
      <!-- Right Image Column (Original Big Size) -->
      <div class="footer__left">
        <img :src="footerImage" alt="Juan Lias" class="footer__image" />
      </div>
      <section class="footer__right">
        <!-- CTA & Contact (Center) -->
        <div class="footer__cta-container">
          <h2 ref="titleRef" class="footer__title">
            <span class="footer__title-line">
              {{ $t('common.footer.cta.title1') }} {{ $t('common.footer.cta.title2') }}
            </span>
            <span ref="accentRef" class="footer__title-accent">
              {{ $t('common.footer.cta.titleAccent') }}
            </span>
          </h2>

          <div
            class="footer__email-wrapper"
            @mouseenter="showEmailActions"
            @mouseleave="hideEmailActions"
          >
            <a
              href="mailto:contact@juanlias.com"
              class="footer__email-link"
              @click="createSparksAt"
            >
              <Mail01Icon class="footer__email-icon" />
              <span>contact@juanlias.com</span>
            </a>
            <div ref="emailActionsRef" class="footer__email-actions">
              <a
                href="mailto:contact@juanlias.com"
                class="footer__email-action"
                aria-label="Send email"
                @click="createSparksAt"
              >
                <MailSend01Icon class="footer__email-action-icon" />
              </a>
              <button
                type="button"
                class="footer__email-action"
                aria-label="Copy email"
                @click="handleCopyEmail"
              >
                <component :is="copyIcon" class="footer__email-action-icon copy-icon" />
              </button>
            </div>
          </div>

          <div class="footer__socials-row">
            <a
              href="https://instagram.com/juanlias_"
              target="_blank"
              rel="noopener noreferrer"
              class="footer__social-link"
              aria-label="Instagram"
            >
              <InstagramIcon class="footer__social-icon" />
            </a>
            <span class="footer__social-dot">•</span>
            <a
              href="https://linkedin.com/in/juanlias"
              target="_blank"
              rel="noopener noreferrer"
              class="footer__social-link"
              aria-label="LinkedIn"
            >
              <Linkedin01Icon class="footer__social-icon" />
            </a>
            <span class="footer__social-dot">•</span>
            <a
              href="https://github.com/juanlias"
              target="_blank"
              rel="noopener noreferrer"
              class="footer__social-link"
              aria-label="GitHub"
            >
              <GithubIcon class="footer__social-icon" />
            </a>
          </div>
        </div>
      </section>
    </div>

    <!-- Bottom Row -->
    <div class="footer__bottom">
      <p class="footer__copyright">&copy; {{ currentYear }} Juan Lias</p>
    </div>
  </footer>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from '@/composables/useTheme'
import {
  InstagramIcon,
  Linkedin01Icon,
  GithubIcon,
  Mail01Icon,
  MailSend01Icon,
  Copy01Icon,
  Tick01Icon,
} from 'hugeicons-vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

import footerImageDark from '@/assets/images/juan-lias-3.webp'
import footerImageLight from '@/assets/images/juan-lias-3-light.webp'

const { locale } = useI18n()
const { isDark } = useTheme()

const footerRef = ref(null)
const titleRef = ref(null)
const accentRef = ref(null)
const emailActionsRef = ref(null)
const copyIcon = ref(Copy01Icon)

let footerScrollTl = null

let sparkContainer = null
let accentMouseTween = null
const isMobile = window.matchMedia('(max-width: 767px)').matches

const handleMouseMoveForAccent = (event) => {
  if (!accentRef.value || isMobile) return

  const vw = window.innerWidth
  const vh = window.innerHeight
  const cx = vw / 2
  const cy = vh / 2

  const deltaX = event.clientX - cx
  const deltaY = event.clientY - cy

  const dist = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
  const maxDist = Math.sqrt(cx * cx + cy * cy)
  const intensity = Math.min(dist / maxDist, 1)

  const offsetX = -deltaX * 0.04
  const offsetY = -deltaY * 0.04

  if (accentMouseTween) accentMouseTween.kill()
  accentMouseTween = gsap.to(accentRef.value, {
    duration: 0.4,
    textShadow: `
      ${offsetX}px ${offsetY}px ${12 + intensity * 16}px rgba(124, 58, 237, ${0.3 + intensity * 0.3}),
      0 0 ${30 + intensity * 30}px rgba(124, 58, 237, ${0.1 + intensity * 0.15})
    `,
    ease: 'power2.out',
  })
}

const cleanupAccentMouse = () => {
  if (accentMouseTween) {
    accentMouseTween.kill()
    accentMouseTween = null
  }
  if (accentRef.value) {
    gsap.set(accentRef.value, { clearProps: 'textShadow' })
  }
}

const createSparksAt = (event) => {
  const rect = event.currentTarget.getBoundingClientRect()
  const clickX = rect.left + rect.width / 2
  const clickY = rect.top + rect.height / 2
  createMagicSparks(clickX, clickY)
}

const createMagicSparks = (clickX, clickY) => {
  if (!sparkContainer) {
    sparkContainer = document.createElement('div')
    sparkContainer.className = 'fixed inset-0 pointer-events-none z-[9999]'
    sparkContainer.style.cssText =
      'position: fixed; top: 0; left: 0; right: 0; bottom: 0; pointer-events: none; z-index: 9999;'
    document.body.appendChild(sparkContainer)
  }

  const sparkCfg = {
    gradient: 'linear-gradient(45deg, #4c00ff 0%, #ffffff 50%, #BFBFBF 100%)',
    boxShadow: '0 0 6px #4c00ff, 0 0 12px rgba(76, 0, 255, 0.5), 0 0 18px rgba(76, 0, 255, 0.3)',
  }

  for (let i = 0; i < 10; i++) {
    const spark = document.createElement('div')
    spark.style.cssText = `
      position: absolute;
      width: 6px; height: 6px;
      border-radius: 50%;
      pointer-events: none;
      background: ${sparkCfg.gradient};
      box-shadow: ${sparkCfg.boxShadow};
      left: ${clickX}px; top: ${clickY}px;
      transform: translate(-50%, -50%) scale(0);
      opacity: 0;
    `
    sparkContainer.appendChild(spark)

    const angle = (i / 10) * Math.PI * 2 + (Math.random() - 0.5) * 0.5
    const distance = 30 + Math.random() * 60
    const endX = Math.cos(angle) * distance
    const endY = Math.sin(angle) * distance

    gsap
      .timeline({
        onComplete: () => {
          if (spark.parentNode) spark.remove()
        },
      })
      .to(spark, { duration: 0.1, scale: 1, opacity: 1, ease: 'power2.out' })
      .to(
        spark,
        {
          duration: 0.5,
          x: endX,
          y: endY,
          scale: 0.3,
          rotation: Math.random() * 360,
          ease: 'power2.out',
        },
        0
      )
      .to(spark, { duration: 0.25, opacity: 0, ease: 'power2.in' }, 0.25)
  }
}

const handleCopyEmail = (event) => {
  const btn = event.currentTarget
  const icon = btn.querySelector('.copy-icon')
  const rect = btn.getBoundingClientRect()
  const clickX = rect.left + rect.width / 2
  const clickY = rect.top + rect.height / 2

  navigator.clipboard.writeText('contact@juanlias.com').then(() => {
    gsap.to(icon, {
      rotation: 90,
      scale: 0,
      opacity: 0,
      duration: 0.3,
      ease: 'back.in(1.7)',
      onComplete: () => {
        copyIcon.value = Tick01Icon

        setTimeout(() => {
          createMagicSparks(clickX, clickY)

          gsap
            .timeline()
            .to(btn, { duration: 0.1, scale: 0.9, ease: 'power2.out' })
            .to(btn, { duration: 0.2, scale: 1.1, ease: 'power2.out' })
            .to(btn, { duration: 0.15, scale: 1, ease: 'power2.out' })

          const newIcon = btn.querySelector('.copy-icon')
          gsap.fromTo(
            newIcon,
            { rotation: -90, scale: 0, opacity: 0 },
            { rotation: 0, scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(1.7)' }
          )

          setTimeout(() => {
            gsap.to(newIcon, {
              rotation: 90,
              scale: 0,
              opacity: 0,
              duration: 0.3,
              ease: 'back.in(1.7)',
              onComplete: () => {
                copyIcon.value = Copy01Icon
                setTimeout(() => {
                  const resetIcon = btn.querySelector('.copy-icon')
                  gsap.fromTo(
                    resetIcon,
                    { rotation: -90, scale: 0, opacity: 0 },
                    { rotation: 0, scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(1.7)' }
                  )
                }, 0)
              },
            })
          }, 2000)
        }, 0)
      },
    })
  })
}

const showEmailActions = () => {
  if (!emailActionsRef.value || window.innerWidth < 1024) return
  gsap.killTweensOf(emailActionsRef.value)
  gsap.to(emailActionsRef.value, {
    clipPath: 'inset(0 0% 0 0)',
    duration: 0.5,
    ease: 'power3.out',
  })
}

const hideEmailActions = () => {
  if (!emailActionsRef.value || window.innerWidth < 1024) return
  gsap.killTweensOf(emailActionsRef.value)
  gsap.to(emailActionsRef.value, {
    clipPath: 'inset(0 100% 0 0)',
    duration: 0.4,
    ease: 'power3.in',
  })
}

// Clock and timezone variables
const curitibaTimeText = ref('')
let timer = null

// Reactive footer image based on current theme mode
const footerImage = computed(() => (isDark.value ? footerImageDark : footerImageLight))

const currentYear = computed(() => new Date().getFullYear())

// Update clock relative to America/Sao_Paulo timezone
const updateCuritibaTime = () => {
  try {
    const now = new Date()
    const lang = locale.value === 'pt' ? 'pt-BR' : locale.value === 'es' ? 'es-ES' : 'en-US'

    const dayStr = now.toLocaleDateString(lang, {
      timeZone: 'America/Sao_Paulo',
      weekday: 'long',
    })

    const timeStr = now.toLocaleTimeString(lang, {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })

    const capitalizedDay = dayStr.charAt(0).toUpperCase() + dayStr.slice(1)

    let statusText = 'Open for collaboration'
    if (locale.value === 'es') {
      statusText = 'Disponible para colaborar'
    } else if (locale.value === 'pt') {
      statusText = 'Disponível para colaboração'
    }

    curitibaTimeText.value = `Curitiba, BR — ${capitalizedDay}, ${timeStr} — ${statusText}`
  } catch (err) {
    console.error('Error formatting Curitiba timezone clock:', err)
    curitibaTimeText.value = 'Curitiba, BR — Open for collaboration'
  }
}

// Watch for locale changes to instantly translate time format and status text
watch(locale, () => {
  updateCuritibaTime()
})

onMounted(() => {
  updateCuritibaTime()
  timer = setInterval(updateCuritibaTime, 60000)

  if (!isMobile) {
    window.addEventListener('mousemove', handleMouseMoveForAccent, { passive: true })
  }

  // Animate footer elements on scroll
  if (footerRef.value) {
    const titleEl = titleRef.value
    const emailEl = footerRef.value.querySelector('.footer__email-wrapper')
    const socialsEl = footerRef.value.querySelector('.footer__socials-row')
    const imageEl = footerRef.value.querySelector('.footer__image')
    const bottomEl = footerRef.value.querySelector('.footer__bottom')

    const elements = [titleEl, emailEl, socialsEl, imageEl, bottomEl].filter(Boolean)

    footerScrollTl = gsap.timeline({
      scrollTrigger: {
        trigger: footerRef.value,
        start: 'top bottom',
        end: 'top center',
        scrub: 0.6,
      },
    })

    footerScrollTl.fromTo(
      elements,
      { opacity: 0, y: 40, filter: 'blur(8px)' },
      { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.7, stagger: 0.1, ease: 'power2.out' }
    )
  }
})

onUnmounted(() => {
  if (footerScrollTl) {
    footerScrollTl.kill()
    footerScrollTl = null
  }
  window.removeEventListener('mousemove', handleMouseMoveForAccent)
  cleanupAccentMouse()

  if (timer) {
    clearInterval(timer)
  }

  if (sparkContainer && sparkContainer.parentNode) {
    sparkContainer.remove()
    sparkContainer = null
  }
})
</script>

<style scoped>
/* ==========================================================================
   FOOTER - Base & Structure
   ========================================================================== */
.footer {
  @apply relative overflow-hidden flex flex-col justify-between p-6 sm:p-10 lg:p-16 min-h-screen lg:h-screen;
  background-color: var(--color-deep-black);
}

.footer__main {
  @apply grid grid-cols-1 lg:grid-cols-2 flex-grow items-center w-full;
  height: 100%;
}

/* ==========================================================================
   LEFT COLUMN - Info, CTA & Contact
   ========================================================================== */
.footer__right {
  @apply flex flex-col justify-between h-full py-4 lg:py-8 md:relative md:z-10;
}

.footer__status {
  @apply text-xs sm:text-sm font-semibold tracking-wider text-text-secondary uppercase mb-8 lg:mb-0;
}

.footer__cta-container {
  @apply flex flex-col items-start gap-6 sm:gap-8 my-auto;
}

.footer__title {
  @apply flex flex-col items-start leading-none gap-2 sm:gap-4;
}

.footer__title-line {
  @apply font-jakarta text-text-primary font-extrabold text-4xl sm:text-5xl xl:text-6xl tracking-tight;
}

.footer__title-accent {
  @apply font-madi font-normal text-6xl sm:text-7xl xl:text-9xl text-electric-purple;
  margin-top: -0.5rem;
  margin-left: -0.25rem;
  will-change: text-shadow;
}

.footer__email-wrapper {
  @apply w-full max-w-max relative mt-2 sm:mt-4 flex items-center gap-3;
}

.footer__email-link {
  @apply font-jakarta text-text-primary font-semibold text-xl sm:text-2xl lg:text-3xl xl:text-4xl tracking-tight;
  @apply transition-colors duration-300 hover:text-electric-purple;
  @apply flex items-end gap-2 sm:gap-3;
}

.footer__email-icon {
  @apply w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 shrink-0;
}

.footer__email-actions {
  @apply flex items-center gap-2 overflow-hidden;
}

@screen lg {
  .footer__email-actions {
    clip-path: inset(0 100% 0 0);
  }
}

.footer__email-action {
  @apply flex items-center justify-center w-10 h-10 rounded-full;
  @apply border-2 border-border text-text-secondary bg-transparent;
  @apply transition-all duration-200 cursor-pointer;
  @apply hover:border-electric-purple hover:text-electric-purple hover:bg-surface-elevated;
}

.footer__email-action-icon {
  @apply w-5 h-5;
}

.footer__socials-row {
  @apply flex flex-wrap items-center gap-3 sm:gap-4 text-sm sm:text-base font-semibold text-text-secondary mt-4 sm:mt-6;
}

.footer__social-link {
  @apply transition-colors duration-300 hover:text-electric-purple;
}

.footer__social-icon {
  @apply w-6 h-6 sm:w-7 sm:h-7;
}

.footer__social-dot {
  @apply text-text-secondary opacity-50;
}

/* ==========================================================================
   RIGHT COLUMN - Big Image
   ========================================================================== */
.footer__left {
  @apply relative overflow-hidden flex items-start justify-center md:absolute md:inset-0 md:z-0 md:pointer-events-none lg:relative lg:items-end lg:h-full;
}

.footer__image {
  @apply w-full h-[40vh] object-cover md:h-full md:w-full md:object-cover md:object-center md:opacity-20 lg:w-auto lg:h-full lg:object-contain lg:object-bottom lg:max-w-full lg:opacity-100 rounded-[3rem];
  object-position: top;
  transition: transform 0.8s cubic-bezier(0.25, 1, 0.5, 1);
  corner-shape: squircle;
}

@screen lg {
  .footer__image {
    object-position: bottom;
    height: 100%;
  }
}

/* ==========================================================================
   BOTTOM ROW - Copyright & Controls
   ========================================================================== */
.footer__bottom {
  @apply flex flex-col sm:flex-row justify-between items-center w-full mt-8 lg:mt-0 pt-6 gap-4 md:relative md:z-10;
}

.footer__copyright {
  @apply text-xs sm:text-sm text-text-secondary text-center sm:text-left opacity-50;
}

.footer__controls {
  @apply flex items-center gap-3 sm:gap-4;
}

.footer__sparkle {
  @apply flex items-center justify-center text-electric-purple opacity-80 ml-1;
  animation: sparkle-float 4s ease-in-out infinite;
}

@keyframes sparkle-float {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-4px) rotate(15deg);
  }
}
</style>
